import { createElement, type ReactNode } from 'react';

import { Checkbox, cn } from '@shigo-ui/ui';

import {
  DashboardDataTableColumnKind,
  type DashboardDataTableColumn,
  type DashboardDataTableColumnDefinition,
  type DashboardDataTableSelectionColumnDefinition,
} from './DashboardDataTable.types';
import { DashboardTableDateCell, DashboardTableStatusBadge } from './DashboardTableCells';
import { DashboardDataTableSelectionHeaderCheckbox } from './DashboardDataTableSelectionHeaderCheckbox';

export function buildDashboardDataTableColumns<TItem>(
  definitions: DashboardDataTableColumnDefinition<TItem>[],
): DashboardDataTableColumn<TItem>[] {
  return definitions.filter((definition) => !definition.hidden).map(buildDashboardDataTableColumn);
}

function buildDashboardDataTableColumn<TItem>(
  definition: DashboardDataTableColumnDefinition<TItem>,
): DashboardDataTableColumn<TItem> {
  const common = {
    id: definition.id,
    header: definition.header ?? '',
    sortable: definition.sortable,
    sortKey: definition.sortKey,
    sticky: definition.sticky,
    stickyOffset: definition.stickyOffset,
  };

  switch (definition.kind) {
    case DashboardDataTableColumnKind.Text:
      return {
        ...common,
        headerClassName: definition.headerClassName,
        className: definition.className,
        cell: (item) => nonEmptyNode(definition.value(item), definition.fallback ?? '-'),
      };
    case DashboardDataTableColumnKind.Date:
      return {
        ...common,
        headerClassName: definition.headerClassName,
        className: definition.className,
        cell: (item) => createElement(DashboardTableDateCell, null, definition.value(item)),
      };
    case DashboardDataTableColumnKind.Status:
      return {
        ...common,
        headerClassName: definition.headerClassName,
        className: definition.className,
        cell: (item) =>
          createElement(
            DashboardTableStatusBadge,
            { tone: definition.tone(item) },
            definition.label(item),
          ),
      };
    case DashboardDataTableColumnKind.Selection:
      return {
        ...common,
        header: selectionHeader(definition),
        headerClassName: cn('w-12 pl-4', definition.headerClassName),
        className: cn('w-12 pl-4', definition.className),
        cell: (item) =>
          createElement(Checkbox, {
            checked: definition.checked(item),
            'aria-label': definition.label(item),
            onCheckedChange: (checked) => {
              definition.onChange(item, checked === true);
            },
          }),
      };
    case DashboardDataTableColumnKind.Actions:
      return {
        ...common,
        headerClassName: cn('w-20 pr-4 text-right', definition.headerClassName),
        className: cn('pr-4 text-right', definition.className),
        cell: definition.cell,
      };
    case DashboardDataTableColumnKind.Custom:
    default:
      return {
        ...common,
        headerClassName: definition.headerClassName,
        className: definition.className,
        cell: definition.cell,
      };
  }
}

function selectionHeader<TItem>(
  definition: DashboardDataTableSelectionColumnDefinition<TItem>,
): ReactNode {
  if (!definition.onHeaderChange) {
    return definition.header ?? '';
  }

  if (!definition.headerLabel) {
    return createElement(Checkbox, {
      checked: definition.headerChecked ?? false,
      disabled: definition.headerDisabled ?? false,
      'aria-label': definition.headerLabel,
      onCheckedChange: (checked) => {
        if (definition.headerDisabled) return;
        definition.onHeaderChange?.(checked === true);
      },
    });
  }

  return createElement(DashboardDataTableSelectionHeaderCheckbox, {
    checked: definition.headerChecked ?? false,
    disabled: definition.headerDisabled ?? false,
    label: definition.headerLabel,
    onCheckedChange: (checked) => {
      if (definition.headerDisabled) return;
      definition.onHeaderChange?.(checked);
    },
  });
}

function nonEmptyNode(value: ReactNode, fallback: ReactNode) {
  return value === null || typeof value === 'undefined' || value === '' ? fallback : value;
}
